import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

let _ = t => t,
    _t;

import { Fragment, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UIButton from 'UIComponents/button/UIButton';
import { H3 } from 'UIComponents/elements/headings';
import UIClickable from 'UIComponents/button/UIClickable';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIImage from 'UIComponents/image/UIImage';
import { KOALA, OLAF, EERIE, GYPSUM } from 'HubStyleTokens/colors';
import { AllProductLines } from 'self-service-api/constants/ProductLines';
import { sortProductLines } from 'pricing-pages-lib/utils/sortingUtils';
import { getAllFeaturesByAllProductLinesSelector } from '../../selectors/FeatureSelectors';
import FeatureTableRow from './FeatureTableRow';
import { getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import breakpoint from '../../constants/Breakpoints';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { ProductToIconMap } from '../../constants/merchCards/ProductToIconMap';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import SuiteFeatureTableStickyHeader from './SuiteFeatureTableStickyHeader';
const TableContainer = styled.div.withConfig({
  displayName: "SuiteFeatureTable__TableContainer",
  componentId: "j4ftgm-0"
})(["width:100%;padding:16px 0 60px;table{width:100%;border-spacing:0;table-layout:fixed;}table thead tr th:nth-child(1),table tbody tr th:nth-child(1){width:", ";border:1px solid ", ";}"], ({
  columns
}) => columns < 3 ? '50%' : '30%', KOALA);
const paddingLeft = css(["padding-left:42px;", ""], breakpoint.tablet(_t || (_t = _`
    padding-left: 16px;
    `)));
const rowHeaderStyles = css(["text-align:left;", ""], paddingLeft);
const Table = styled.table.withConfig({
  displayName: "SuiteFeatureTable__Table",
  componentId: "j4ftgm-1"
})(["tbody tr{display:table;width:100%;table-layout:fixed;min-height:70px;}"]);
const ExpandableSectionLabel = styled.tbody.withConfig({
  displayName: "SuiteFeatureTable__ExpandableSectionLabel",
  componentId: "j4ftgm-2"
})(["background-color:", ";tr td:nth-child(1) div > div{width:100%;text-align:left;padding-top:8px;padding-bottom:8px;", " display:flex;align-items:flex-end;}"], GYPSUM, paddingLeft);
const ExpandableTableContent = styled.tbody.withConfig({
  displayName: "SuiteFeatureTable__ExpandableTableContent",
  componentId: "j4ftgm-3"
})(["position:relative;overflow:hidden;display:block;max-height:", ";transition:max-height 0.2s linear;&::after{visibility:", ";opacity:", ";position:absolute;bottom:0;height:100%;width:100%;content:'';background:linear-gradient(to bottom,transparent 70%,", ");pointer-events:none;}& tr td{padding:16px 0;text-align:center;border:1px solid ", ";}& tr th[scope='row']{", "}"], props => props.isSectionExpanded ? props.isFeatureTableExpanded ? '100%' : '350px' : '0', props => props.isFeatureTableExpanded ? 'hidden' : 'visible', props => props.isFeatureTableExpanded ? 0 : 1, OLAF, KOALA, rowHeaderStyles);
const ExpandTableButtonContainer = styled.tbody.withConfig({
  displayName: "SuiteFeatureTable__ExpandTableButtonContainer",
  componentId: "j4ftgm-4"
})(["& tr td{padding-bottom:24px;", "}"], paddingLeft);
const initialSectionState = AllProductLines.reduce((agg, curr) => {
  agg[curr] = true;
  return agg;
}, {});
const initialFeatureTableState = AllProductLines.reduce((agg, curr) => {
  agg[curr] = false;
  return agg;
}, {});

const SuiteFeatureTable = ({
  productsByProductLine,
  screen,
  tab,
  isSuiteStarterPage,
  trackingSource,
  titleKey,
  isLearnMoreModal = false
}) => {
  const [isSectionExpanded, setIsSectionExpanded] = useState(initialSectionState);
  const [isFeatureTableExpanded, setIsFeatureTableExpanded] = useState(initialFeatureTableState);
  const features = useSelector(getAllFeaturesByAllProductLinesSelector);
  const featureTableRef = useRef(null);

  const handleExpandSectionChange = productLine => {
    getUsageTracker().track('featureTableInteraction', {
      action: `clicked-${isSectionExpanded[productLine] ? 'collapse' : 'expand'}-section`,
      productLine,
      products: productsByProductLine[productLine],
      screen,
      tab,
      trackingSource
    });
    setIsSectionExpanded(prev => {
      return Object.assign({}, prev, {
        [productLine]: !prev[productLine]
      });
    });
  };

  const handleExpandFeatureTableChange = productLine => {
    getUsageTracker().track('featureTableInteraction', {
      action: `clicked-${isFeatureTableExpanded[productLine] ? 'collapse' : 'expand'}-table`,
      productLine,
      products: productsByProductLine[productLine],
      screen,
      tab,
      trackingSource
    });
    setIsFeatureTableExpanded(prev => {
      return Object.assign({}, prev, {
        [productLine]: !prev[productLine]
      });
    });
  };

  if (!Object.keys(features).length) return null;
  const sortedProductLines = sortProductLines(Object.keys(features));
  const levels = new Set();
  Object.values(productsByProductLine).forEach(products => {
    products.forEach(product => {
      const level = getProductLevelFromApiNameString(product);
      levels.add(level);
    });
  });
  return /*#__PURE__*/_jsxs(TableContainer, {
    ref: featureTableRef,
    columns: levels.size,
    "data-selenium": "suite-feature-table",
    children: [/*#__PURE__*/_jsx("div", {
      className: "text-center m-y-5",
      children: /*#__PURE__*/_jsx(H3, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: titleKey || 'suiteFeatureTable.title'
        })
      })
    }), /*#__PURE__*/_jsx(SuiteFeatureTableStickyHeader, {
      levels: [...levels],
      tableRef: featureTableRef,
      isSuiteStarterPage: isSuiteStarterPage,
      isLearnMoreModal: isLearnMoreModal
    }), /*#__PURE__*/_jsx(Table, {
      children: sortedProductLines.map(productLine => {
        const localFeatures = features[productLine];
        const highlightedProducts = productsByProductLine[productLine];
        if (!highlightedProducts.length) return null;
        const highlightedProductsSet = new Set(highlightedProducts);
        return /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(ExpandableSectionLabel, {
            children: /*#__PURE__*/_jsx("tr", {
              children: /*#__PURE__*/_jsx("td", {
                colSpan: levels.size + 1,
                children: /*#__PURE__*/_jsx(UIClickable, {
                  onClick: () => handleExpandSectionChange(productLine),
                  "aria-expanded": isSectionExpanded[productLine] ? 'true' : 'false',
                  "aria-controls": `suite-feature-table-${productLine}-section`,
                  children: /*#__PURE__*/_jsxs("div", {
                    className: "display-flex align-center",
                    children: [/*#__PURE__*/_jsx(UIImage, {
                      src: ProductToIconMap[productLine][STARTER],
                      width: "24",
                      className: "m-right-3"
                    }), /*#__PURE__*/_jsx("strong", {
                      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                        message: `suiteFeatureTable.section.title`,
                        options: {
                          product: /*#__PURE__*/_jsx(FormattedMessage, {
                            message: `productLine.${productLine}`
                          })
                        }
                      })
                    })]
                  })
                })
              })
            })
          }), /*#__PURE__*/_jsx(ExpandableTableContent, {
            id: `suite-feature-table-${productLine}-section`,
            isSectionExpanded: isSectionExpanded[productLine],
            isFeatureTableExpanded: isFeatureTableExpanded[productLine],
            children: localFeatures.map(feature => {
              return /*#__PURE__*/_jsx(FeatureTableRow, {
                highlightedProducts: highlightedProducts,
                feature: feature,
                productLine: productLine,
                highlightedProductsSet: highlightedProductsSet
              }, feature.featureId);
            })
          }), isSectionExpanded[productLine] && /*#__PURE__*/_jsx(ExpandTableButtonContainer, {
            children: /*#__PURE__*/_jsx("tr", {
              children: /*#__PURE__*/_jsx("td", {
                colSpan: levels.size + 1,
                className: "display-flex align-center",
                children: /*#__PURE__*/_jsxs(UIButton, {
                  use: "transparent",
                  onClick: () => {
                    handleExpandFeatureTableChange(productLine);
                  },
                  "aria-expanded": isSectionExpanded[productLine] ? 'true' : 'false',
                  className: "p-left-8",
                  children: [/*#__PURE__*/_jsx(FormattedMessage, {
                    message: isFeatureTableExpanded[productLine] ? `suiteFeatureTable.ebx633Collapse` : `suiteFeatureTable.ebx633Expand`
                  }), /*#__PURE__*/_jsx(UIIcon, {
                    name: isFeatureTableExpanded[productLine] ? 'upCarat' : 'downCarat',
                    color: EERIE
                  })]
                })
              })
            })
          })]
        }, productLine);
      })
    })]
  });
};

export default SuiteFeatureTable;